import React from "react";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import {
  CurrencyCircleDollar,
  Stack,
  GridFour,
  Megaphone,
  DeviceMobile,
  Database,
  ChartPieSlice,
  Lightning,
  CheckCircle,
  ArrowRight,
} from "@phosphor-icons/react";

const MOBILE_DASH =
  "https://static.prod-images.emergentagent.com/jobs/6d7cad75-b3f1-423c-a893-00a3231cc41c/images/4f419fc0f102634deeade9fe8a9a4539c40f60c586163ef5ad06d74d79f727a5.png";
const STORE_AISLE =
  "https://customer-assets.emergentagent.com/job_modern-merchandising-1/artifacts/lyoq0mmp_Screenshot%202026-05-04%20at%2013.40.32.png";
const DASH_CHARTS =
  "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?fm=jpg&q=70&w=2400&auto=format&fit=crop";
const MERCH_SHELF = "/instore/mervik-app.jpg";

const Eyebrow = ({ children, accent = false }) => (
  <span className={`gm-eyebrow ${accent ? "text-[#0A4DFF]" : ""}`}>{children}</span>
);

const SectionTitle = ({ kicker, title, accent = false }) => (
  <div className="max-w-3xl">
    <Eyebrow accent={accent}>{kicker}</Eyebrow>
    <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{title}</h2>
  </div>
);

const Hero = () => {
  const { t } = useLang();
  return (
    <section className="relative bg-white border-b border-[#e5e5e5]" data-testid="instore-hero">
      <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-12 md:pb-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <Eyebrow accent>{t.instore.hero.eyebrow}</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6">
              {t.instore.hero.title}
            </h1>
            <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-2xl leading-relaxed">
              {t.instore.hero.sub}
            </p>
            <p className="text-base text-[#0a0a0a] mt-6 max-w-2xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
              {t.instore.hero.leadIn}
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="aspect-square overflow-hidden border border-[#e5e5e5] bg-[#fafafa]">
              <img src={MOBILE_DASH} alt="MERVIK mobile dashboard" className="w-full h-full object-cover" />
            </div>
            <div className="mt-3 flex items-center justify-between font-mono text-[10px] tracking-widest text-[#525252]">
              <span>FIG. 01</span>
              <span>MERVIK · FIELD APP</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PriceSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.price;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="section-price">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">{s.title}</h2>
          <div className="mt-8 inline-flex items-center justify-center w-14 h-14 bg-[#0A4DFF] text-white">
            <CurrencyCircleDollar size={26} weight="thin" />
          </div>

          {/* Price comparison chart — Reponio-style */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Card 1 — Your brand */}
            <div className="bg-white border border-[#e5e5e5] p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between text-[11px] tracking-wide text-[#525252]">
                <span>Vaše značka · avg. cena</span>
                <span className="flex items-center gap-1.5 text-[#0A4DFF] font-medium">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 8V2M5 2L2 5M5 2l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  −2.1%
                </span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-2xl md:text-3xl tracking-tight">42,90 Kč</span>
              </div>
              <svg viewBox="0 0 320 110" className="w-full h-20 mt-3" aria-hidden="true">
                <defs>
                  <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A4DFF" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#0A4DFF" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,70 C40,60 60,80 90,72 S150,40 180,55 240,75 270,55 320,40 320,40"
                  fill="none"
                  stroke="#0A4DFF"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <path
                  d="M0,70 C40,60 60,80 90,72 S150,40 180,55 240,75 270,55 320,40 320,40 L320,110 L0,110 Z"
                  fill="url(#gradBlue)"
                />
                <circle cx="320" cy="40" r="4" fill="#0A4DFF" />
                <circle cx="320" cy="40" r="8" fill="#0A4DFF" opacity="0.18" />
              </svg>
              <div className="flex items-center justify-between text-[10px] font-mono tracking-widest text-[#9a9a9a] mt-1">
                <span>1. 5.</span>
                <span>30. 5.</span>
              </div>
            </div>

            {/* Card 2 — Competitor */}
            <div className="bg-white border border-[#e5e5e5] p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between text-[11px] tracking-wide text-[#525252]">
                <span>Konkurence · avg. cena</span>
                <span className="flex items-center gap-1.5 text-[#FF3B30] font-medium">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 2v6M5 8L2 5M5 8l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  +4.7%
                </span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-2xl md:text-3xl tracking-tight">39,50 Kč</span>
              </div>
              <svg viewBox="0 0 320 110" className="w-full h-20 mt-3" aria-hidden="true">
                <defs>
                  <linearGradient id="gradRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF3B30" stopOpacity="0.14" />
                    <stop offset="100%" stopColor="#FF3B30" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,40 C40,55 60,35 90,45 S150,75 180,65 240,80 270,72 320,80 320,80"
                  fill="none"
                  stroke="#FF3B30"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <path
                  d="M0,40 C40,55 60,35 90,45 S150,75 180,65 240,80 270,72 320,80 320,80 L320,110 L0,110 Z"
                  fill="url(#gradRed)"
                />
                <circle cx="320" cy="80" r="4" fill="#FF3B30" />
                <circle cx="320" cy="80" r="8" fill="#FF3B30" opacity="0.18" />
              </svg>
              <div className="flex items-center justify-between text-[10px] font-mono tracking-widest text-[#9a9a9a] mt-1">
                <span>1. 5.</span>
                <span>30. 5.</span>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-7 space-y-6">
          {s.body.map((p, i) => (
            <p key={i} className="text-[#0a0a0a] text-base leading-relaxed">{p}</p>
          ))}
          <ul className="border-t border-[#0a0a0a] divide-y divide-[#e5e5e5]">
            {s.checklist.map((c, i) => (
              <li key={i} className="flex items-start gap-4 py-4">
                <CheckCircle size={18} className="text-[#0A4DFF] mt-0.5 shrink-0" weight="thin" />
                <span className="text-sm md:text-base leading-relaxed">{c}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm md:text-base text-[#525252] italic leading-relaxed mt-2">{s.footer}</p>
        </div>
      </div>
    </section>
  );
};

const ShelfSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.shelf;
  return (
    <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="section-shelf">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-6">
            <div className="aspect-[4/5] overflow-hidden border border-[#e5e5e5]">
              <img src={STORE_AISLE} alt="Shelf monitoring" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6">
            <Eyebrow>{s.eyebrow}</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">{s.title}</h2>
            <div className="space-y-5 mt-8">
              {s.body.map((p, i) => (
                <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mt-8 border-t border-[#0a0a0a] pt-6">
              {s.checklist.map((c, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Stack size={16} className="text-[#0A4DFF] mt-1 shrink-0" weight="thin" />
                  <span className="text-sm leading-relaxed">{c}</span>
                </div>
              ))}
            </div>
            <p className="text-[#525252] italic mt-6 text-sm leading-relaxed">{s.footer}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const TwoColSection = ({ kicker, title, body, twoCol, Icon, testid, sectionClass = "py-14 md:py-20 bg-[#fafafa]" }) => (
  <section className={sectionClass} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-start gap-6">
        {Icon && (
          <div className="hidden md:inline-flex w-14 h-14 bg-[#0a0a0a] text-white items-center justify-center shrink-0">
            <Icon size={26} weight="thin" />
          </div>
        )}
        <div>
          <Eyebrow>{kicker}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-3 leading-[1.05] max-w-3xl">{title}</h2>
        </div>
      </div>
      <div className="space-y-5 mt-10 max-w-3xl">
        {body.map((p, i) => (
          <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
        {twoCol.map((c, i) => (
          <div key={i} className="bg-white border border-[#e5e5e5] p-8 md:p-10">
            <span className="font-mono text-xs tracking-widest text-[#525252]">{`0${i + 1}`}</span>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-3">{c.title}</h3>
            <p className="text-[#525252] mt-4 leading-relaxed">{c.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SecondarySection = () => {
  const { t } = useLang();
  const s = t.instore.sections.secondary;
  return (
    <TwoColSection
      kicker={s.eyebrow}
      title={s.title}
      body={s.body}
      twoCol={s.twoCol}
      Icon={GridFour}
      testid="section-secondary"
    />
  );
};

const PosSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.pos;
  return (
    <TwoColSection
      kicker={s.eyebrow}
      title={s.title}
      body={s.body}
      twoCol={s.twoCol}
      Icon={Megaphone}
      testid="section-pos"
      sectionClass="pt-4 md:pt-6 pb-14 md:pb-20 bg-[#fafafa]"
    />
  );
};

const DataPointsSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.datapoints;
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="section-datapoints">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <span className="gm-eyebrow text-white/50">{s.eyebrow}</span>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">{s.title}</h2>
          </div>
          <p className="md:col-span-5 text-white/70 leading-relaxed">{s.intro}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-14 border border-white/10">
          {s.groups.map((g, i) => (
            <div key={i} className="p-8 md:p-10 border-b md:border-b-0 last:border-b-0 border-white/10 lg:border-r last:lg:border-r-0">
              <span className="font-mono text-[10px] tracking-widest text-white/40">{`G.0${i + 1}`}</span>
              <h3 className="font-display text-xl md:text-2xl mt-4 leading-tight">{g.h}</h3>
              <p className="text-sm text-white/60 mt-4 leading-relaxed">{g.t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SystemRow = ({ kicker, title, body, Icon, image, reverse = false, bullets, testid }) => (
  <section className="py-14 md:py-20 bg-white" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
        <div className="md:col-span-6">
          <div className="aspect-[4/3] overflow-hidden border border-[#e5e5e5]">
            <img src={image} alt={title} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="flex items-center gap-3">
            {Icon && <Icon size={22} className="text-[#0A4DFF]" weight="thin" />}
            <Eyebrow>{kicker}</Eyebrow>
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05]">{title}</h2>
          {Array.isArray(body) ? (
            <div className="space-y-5 mt-8">
              {body.map((p, i) => (
                <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
              ))}
            </div>
          ) : (
            <p className="text-[#0a0a0a] mt-8 leading-relaxed">{body}</p>
          )}
          {bullets && (
            <ul className="mt-8 border-t border-[#0a0a0a] divide-y divide-[#e5e5e5]">
              {bullets.map((b, i) => (
                <li key={i} className="py-3 flex items-start gap-3">
                  <Lightning size={16} className="text-[#0A4DFF] mt-1 shrink-0" weight="thin" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  </section>
);

const MervikSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.mervik;
  return (
    <SystemRow
      kicker={s.eyebrow}
      title={s.title}
      body={s.body}
      Icon={DeviceMobile}
      image={MERCH_SHELF}
      testid="section-mervik"
    />
  );
};

const ProcessIntro = () => {
  const { t } = useLang();
  const s = t.instore.sections.processIntro;
  return (
    <section className="py-14 md:py-20 bg-[#0A4DFF] text-white" data-testid="section-process-intro">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <span className="font-mono uppercase tracking-[0.22em] text-[12px] font-medium text-white/80">
          {s.eyebrow}
        </span>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-4xl">
          {s.title}
        </h2>
        <p className="text-white/80 mt-5 max-w-3xl leading-relaxed text-base md:text-lg">{s.sub}</p>
      </div>
    </section>
  );
};

const SymesSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.symes;
  return (
    <SystemRow
      kicker={s.eyebrow}
      title={s.title}
      body={s.body}
      bullets={s.bullets}
      Icon={Database}
      image={DASH_CHARTS}
      reverse
      testid="section-symes"
    />
  );
};

const QlikSection = () => {
  const { t } = useLang();
  const s = t.instore.sections.qlik;
  return (
    <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="section-qlik">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7 space-y-5">
          <div className="flex items-center gap-3">
            <ChartPieSlice size={22} className="text-[#0A4DFF]" weight="thin" />
            <Eyebrow accent>{s.eyebrow}</Eyebrow>
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95] font-medium">{s.title}</h2>
          {s.body.map((p, i) => (
            <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
          ))}
        </div>
        <div className="md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden border border-[#0a0a0a] bg-[#0a0a0a]">
            <img src="/instore/qlik-dashboard.jpg" alt="QlikView dashboard — analytická platforma" className="w-full h-full object-cover" />
          </div>
          <div className="mt-3 flex items-center justify-between font-mono text-[10px] tracking-widest text-[#525252]">
            <span>FIG. 02</span>
            <span>QLIKVIEW · ASSOCIATIVE MODEL</span>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

const WhySection = () => {
  const { t } = useLang();
  const s = t.instore.sections.why;
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="section-why">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Eyebrow accent>{s.eyebrow}</Eyebrow>
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium">{s.title}</h2>
        <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-3xl">{s.body}</p>
      </div>
    </section>
  );
};

const CtaSection = () => {
  const { t } = useLang();
  const s = t.instore.cta;
  return (
    <section id="contact" className="py-14 md:py-20 bg-white border-t border-[#e5e5e5] scroll-mt-24" data-testid="section-cta">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <Eyebrow accent>{s.eyebrow}</Eyebrow>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium">{s.title}</h2>
          <p className="text-[#525252] mt-6 leading-relaxed max-w-2xl">{s.sub}</p>
        </div>
        <div className="md:col-span-5">
          <div className="bg-[#0a0a0a] text-white p-8">
            <div className="font-mono text-[10px] tracking-widest text-white/50">// REACH US</div>
            <ul className="mt-6 border-t border-white/20">
              <li className="py-4 border-b border-white/10">
                <div className="font-mono text-[10px] tracking-widest text-white/50">E-MAIL</div>
                <a href="mailto:info@globalmarketing.cz" className="font-display text-xl md:text-2xl hover:text-[#0A4DFF] transition-colors break-all mt-1 inline-block">info@globalmarketing.cz</a>
              </li>
              <li className="py-4 border-b border-white/10">
                <div className="font-mono text-[10px] tracking-widest text-white/50">TELEFON</div>
                <a href="tel:+420602111108" className="font-display text-xl md:text-2xl hover:text-[#0A4DFF] transition-colors mt-1 inline-block">+420 602 111 108</a>
              </li>
              <li className="py-4">
                <div className="font-mono text-[10px] tracking-widest text-white/50">SÍDLO</div>
                <div className="font-display text-xl md:text-2xl mt-1">Praha · Bratislava</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function InStoreData() {
  return (
    <div data-testid="instore-page">
      <Seo
        title={{ cs: "InStore Data · Price & Shelf Monitoring", en: "InStore Data · Price & Shelf Monitoring" }}
        description={{
          cs:
            "Vlastní systémy pro sběr a analýzu instore dat. Price & Shelf Monitoring, druhotná umístění, POS — MERVIK, SYMES a QlikView.",
          en:
            "Proprietary systems for collecting and analysing instore data. Price & Shelf Monitoring, secondary placements, POS — MERVIK, SYMES and QlikView.",
        }}
        path="/instore-data"
      />
      <Hero />
      <PriceSection />
      <ShelfSection />
      <SecondarySection />
      <PosSection />
      <DataPointsSection />
      <MervikSection />
      <SymesSection />
      <QlikSection />
      <WhySection />
      <CtaSection />
    </div>
  );
}

